// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import type { Alarm } from '../types/alarm';
import { isClearedState } from '../types/alarmFsm';

/** Asset id → its open alarms, from the alarm service. */
export type AlarmsByAsset = ReadonlyMap<string, Alarm[]>;

/** The mapper's placeholder for an alarm with no linked SOP; never worth a line. */
export const alarmSummary = (alarm: Alarm): string | undefined =>
  alarm.summary && alarm.summary !== 'Unknown' ? alarm.summary : undefined;

/** What the alarm is about, when anything upstream says: rule title, SOP summary or diagnosis. */
export const alarmTitle = (alarm: Alarm): string | undefined =>
  alarm.ruleTitle || alarmSummary(alarm) || alarm.issue || undefined;

/** `P1 · Summary · 12m ago` — one hover line per open alarm. */
export const alarmLine = (alarm: Alarm): string =>
  [
    alarm.sev !== 'Unknown' ? alarm.sev : '',
    alarmTitle(alarm) ?? alarm.id,
    alarm.raisedAgo === undefined
      ? ''
      : alarm.raisedAgo < 60
        ? `${alarm.raisedAgo}m ago`
        : `${Math.floor(alarm.raisedAgo / 60)}h ago`,
  ]
    .filter(Boolean)
    .join(' · ');

/**
 * Open alarms grouped by asset, keyed on both the equipment id and the human
 * equipment key so whichever one the twin uses as its id hits.
 *
 * `entityIdOf` maps an equipment name, lower-cased, to the twin entity id
 * that carries it as `upstreamId` (`PDU-3-A` → its GUID). AEM names equipment
 * the way the site does, while the diagram looks assets up by entity id, so
 * an alarm is also filed under the entity it names.
 */
export const alarmsByAsset = (
  alarms: readonly Alarm[],
  entityIdOf?: ReadonlyMap<string, string>
): Map<string, Alarm[]> => {
  const out = new Map<string, Alarm[]>();
  for (const alarm of alarms) {
    // A cleared alarm is open for the operator (it waits to be closed) but its
    // condition is gone, so it no longer flags the asset.
    if (alarm.closed || isClearedState(alarm.status)) continue;
    const names = [alarm.assetId, alarm.asset].filter(Boolean) as string[];
    const entityIds = names.map(name => entityIdOf?.get(name.toLowerCase()));
    for (const key of Array.from(new Set([...names, ...entityIds].filter(Boolean) as string[]))) {
      const list = out.get(key) ?? [];
      list.push(alarm);
      out.set(key, list);
    }
  }
  return out;
};

/** Worst first; an alarm with no severity sorts last. */
const SEVERITY_RANK: Record<string, number> = { P1: 0, P2: 1, P3: 2, P4: 3 };
const severityRank = (alarm: Alarm): number => SEVERITY_RANK[alarm.sev] ?? 4;

/** Every open alarm on any of `ids`, deduped by row, worst severity first. */
export const alarmsFor = (alarmsOf: AlarmsByAsset | undefined, ids: readonly string[]): Alarm[] => {
  const seen = new Set<string>();
  const out: Alarm[] = [];
  for (const id of ids) {
    for (const alarm of alarmsOf?.get(id) ?? []) {
      if (seen.has(alarm.uid)) continue;
      seen.add(alarm.uid);
      out.push(alarm);
    }
  }
  return out.sort((a, b) => severityRank(a) - severityRank(b));
};

/** The hover title: the element's own line, then one line per alarm. */
export const withAlarmLines = (
  title: string | undefined,
  lines: readonly string[] | undefined
): string | undefined => {
  if (!lines?.length) return title;
  return [title, ...lines].filter(Boolean).join('\n');
};
